import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';

// ─── Animated Counter Component ────────────────────────────────────────
function Counter({ end, suffix = '', duration = 2000 }: { end: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = performance.now();
          const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * end));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <div ref={ref}>{count.toLocaleString()}{suffix}</div>;
}

// ─── Scroll Reveal Component ───────────────────────────────────────────
function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Main Homepage3 Component ──────────────────────────────────────────
export function Homepage3() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      setMousePos({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight });
    };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  return (
    <div style={styles.page}>
      <style>{keyframes}</style>

      {/* ─── Navbar ─────────────────────────────────────────── */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <Link to="/homepage-3" style={styles.logoLink}>
            <Logo size="md" />
          </Link>
          <div style={styles.navLinks}>
            <a href="#features" style={styles.navLink}>Features</a>
            <a href="#demo" style={styles.navLink}>Demo</a>
            <a href="#testimonials" style={styles.navLink}>Testimonials</a>
            <Link to="/auth" style={styles.navCta}>Get Started</Link>
          </div>
        </div>
      </nav>

      {/* ─── Hero Section (Full Screen Animated) ────────────── */}
      <section style={styles.hero}>
        {/* Animated background nodes */}
        <div style={styles.heroBackground}>
          <div style={{ ...styles.floatingNode, ...styles.node1, transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)` }} />
          <div style={{ ...styles.floatingNode, ...styles.node2, transform: `translate(${-mousePos.x * 15}px, ${mousePos.y * 15}px)` }} />
          <div style={{ ...styles.floatingNode, ...styles.node3, transform: `translate(${mousePos.x * 10}px, ${-mousePos.y * 10}px)` }} />
          <div style={{ ...styles.floatingNode, ...styles.node4, transform: `translate(${-mousePos.x * 25}px, ${-mousePos.y * 12}px)` }} />
          <div style={{ ...styles.floatingNode, ...styles.node5, transform: `translate(${mousePos.x * 18}px, ${mousePos.y * 8}px)` }} />
          {/* Connecting lines */}
          <svg style={styles.heroSvg} viewBox="0 0 1200 800" preserveAspectRatio="none">
            <line x1="200" y1="200" x2="600" y2="300" style={styles.connectLine} />
            <line x1="600" y1="300" x2="900" y2="150" style={styles.connectLine} />
            <line x1="900" y1="150" x2="1000" y2="500" style={styles.connectLine} />
            <line x1="200" y1="200" x2="400" y2="600" style={styles.connectLine} />
            <line x1="400" y1="600" x2="1000" y2="500" style={styles.connectLine} />
          </svg>
        </div>

        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            <span style={styles.heroTitleLine1}>Create with</span>
            <span style={styles.heroTitleGradient}>Visual AI Workflows</span>
          </h1>
          <p style={styles.heroSubtitle}>
            Connect prompts, images, and presets on an infinite canvas.
            Generate stunning visuals with up to 14 reference images per generation.
          </p>
          <Link to="/auth" style={styles.heroCta}>
            <span>Start Creating</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>

        {/* Scroll indicator */}
        <div style={styles.scrollIndicator}>
          <div style={styles.scrollDot} />
        </div>
      </section>

      {/* ─── Social Proof Metrics ───────────────────────────── */}
      <section style={styles.metricsSection}>
        <div style={styles.metricsGrid}>
          {[
            { value: 10000, suffix: '+', label: 'Images Generated' },
            { value: 500, suffix: '+', label: 'Creators' },
            { value: 14, suffix: '', label: 'Reference Images per Generation' },
            { value: 0, suffix: '', label: 'Real-time Collaboration', isText: true },
          ].map((metric, i) => (
            <Reveal key={i} delay={i * 100}>
              <div style={styles.metricCard}>
                <div style={styles.metricValue}>
                  {metric.isText ? (
                    <span>Live</span>
                  ) : (
                    <Counter end={metric.value} suffix={metric.suffix} />
                  )}
                </div>
                <div style={styles.metricLabel}>{metric.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── Product Screenshots ────────────────────────────── */}
      <section style={styles.screenshotSection} id="features">
        <Reveal>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>The Canvas Experience</h2>
            <p style={styles.sectionSubtitle}>Build complex image generation workflows visually</p>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div style={styles.screenshotContainer}>
            {/* Mock canvas interface */}
            <div style={styles.canvasMock}>
              <div style={styles.canvasToolbar}>
                <div style={styles.toolbarDots}>
                  <span style={{ ...styles.dot, background: '#ff5f57' }} />
                  <span style={{ ...styles.dot, background: '#febc2e' }} />
                  <span style={{ ...styles.dot, background: '#28c840' }} />
                </div>
                <span style={styles.toolbarTitle}>Cascady Canvas</span>
              </div>
              <div style={styles.canvasArea}>
                {/* Mock nodes */}
                <div style={{ ...styles.mockNode, top: '15%', left: '10%' }}>
                  <div style={styles.mockNodeHeader}>Prompt</div>
                  <div style={styles.mockNodeBody}>A serene mountain landscape at golden hour...</div>
                </div>
                <div style={{ ...styles.mockNode, top: '10%', left: '55%' }}>
                  <div style={styles.mockNodeHeader}>Reference</div>
                  <div style={styles.mockNodeImage}>
                    <div style={styles.mockImagePlaceholder}>🖼️</div>
                  </div>
                </div>
                <div style={{ ...styles.mockNode, top: '55%', left: '30%' }}>
                  <div style={styles.mockNodeHeader}>Style Preset</div>
                  <div style={styles.mockNodeBody}>Cinematic, HDR, 4K</div>
                </div>
                <div style={{ ...styles.mockNode, top: '50%', left: '70%', borderColor: '#4ADE80' }}>
                  <div style={{ ...styles.mockNodeHeader, background: '#4ADE80' }}>Generate</div>
                  <div style={styles.mockNodeImage}>
                    <div style={{ ...styles.mockImagePlaceholder, background: 'linear-gradient(135deg, #4ADE80 0%, #2dd4bf 100%)' }}>✨</div>
                  </div>
                </div>
                {/* Mock edges */}
                <svg style={styles.canvasSvg}>
                  <path d="M 180 80 C 300 80, 350 80, 420 65" style={styles.mockEdge} />
                  <path d="M 180 80 C 250 200, 280 260, 300 280" style={styles.mockEdge} />
                  <path d="M 420 100 C 500 200, 550 260, 560 280" style={styles.mockEdge} />
                  <path d="M 350 310 C 420 310, 480 300, 540 290" style={styles.mockEdge} />
                </svg>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── Feature Cards ──────────────────────────────────── */}
      <section style={styles.featuresSection}>
        <Reveal>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Powerful Capabilities</h2>
            <p style={styles.sectionSubtitle}>Everything you need to create stunning AI-generated imagery</p>
          </div>
        </Reveal>

        <div style={styles.featuresGrid}>
          {[
            { icon: '🎨', title: 'Visual Canvas', desc: 'Drag-and-drop nodes on an infinite whiteboard. Connect prompts, images, and presets intuitively.' },
            { icon: '🔗', title: 'Multi-Reference', desc: 'Use up to 14 reference images per generation for unprecedented control over your output.' },
            { icon: '⚡', title: 'Real-time Generation', desc: 'See results instantly with Gemini-powered generation. Iterate faster than ever before.' },
            { icon: '🎭', title: 'Style Presets', desc: 'Apply cinematic, artistic, or photorealistic styles with one click. Create and share custom presets.' },
            { icon: '👥', title: 'Team Collaboration', desc: 'Work together on canvases in real-time. Share workflows and build on each other\'s ideas.' },
            { icon: '📁', title: 'Gallery & History', desc: 'Every generation is saved. Browse, compare, and evolve your visual ideas over time.' },
          ].map((feature, i) => (
            <Reveal key={i} delay={i * 80}>
              <div style={styles.featureCard} className="feature-card">
                <div style={styles.featureIcon}>{feature.icon}</div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDesc}>{feature.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── Video/Demo Section ─────────────────────────────── */}
      <section style={styles.demoSection} id="demo">
        <Reveal>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>See Cascady in Action</h2>
            <p style={styles.sectionSubtitle}>Watch how creators build complex workflows in minutes</p>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div style={styles.videoContainer}>
            <div style={styles.videoPlaceholder}>
              <div style={styles.playButton}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <p style={styles.videoLabel}>Product Demo Coming Soon</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── Testimonials ───────────────────────────────────── */}
      <section style={styles.testimonialsSection} id="testimonials">
        <Reveal>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Loved by Creators</h2>
            <p style={styles.sectionSubtitle}>See what our community is saying</p>
          </div>
        </Reveal>

        <div style={styles.testimonialsGrid}>
          {[
            { quote: 'Cascady completely changed how I approach AI art. The canvas interface makes complex workflows feel natural.', name: 'Sarah Chen', role: 'Digital Artist' },
            { quote: 'Being able to use 14 reference images gives me control I never had before. The results are incredible.', name: 'Marcus Rivera', role: 'Creative Director' },
            { quote: 'Our team collaborates on canvases daily. It\'s become essential to our creative pipeline.', name: 'Aisha Patel', role: 'Studio Lead' },
          ].map((testimonial, i) => (
            <Reveal key={i} delay={i * 120}>
              <div style={styles.testimonialCard}>
                <div style={styles.quoteIcon}>"</div>
                <p style={styles.testimonialQuote}>{testimonial.quote}</p>
                <div style={styles.testimonialAuthor}>
                  <div style={styles.authorAvatar}>{testimonial.name[0]}</div>
                  <div>
                    <div style={styles.authorName}>{testimonial.name}</div>
                    <div style={styles.authorRole}>{testimonial.role}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── Final CTA ──────────────────────────────────────── */}
      <section style={styles.ctaSection}>
        <Reveal>
          <h2 style={styles.ctaTitle}>Ready to Create?</h2>
          <p style={styles.ctaSubtitle}>Join thousands of creators building with visual AI workflows.</p>
          <Link to="/auth" style={styles.ctaButton}>
            <span>Get Started Free</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </Reveal>
      </section>

      {/* ─── Footer ─────────────────────────────────────────── */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={styles.footerBrand}>
            <Logo size="md" />
            <p style={styles.footerText}>Visual AI image generation canvas.</p>
          </div>
          <div style={styles.footerLinks}>
            <Link to="/privacy" style={styles.footerLink}>Privacy</Link>
            <Link to="/terms" style={styles.footerLink}>Terms</Link>
            <Link to="/contact" style={styles.footerLink}>Contact</Link>
          </div>
        </div>
        <div style={styles.footerCopyright}>© 2026 Cascady. All rights reserved.</div>
      </footer>
    </div>
  );
}

// ─── CSS Keyframes ─────────────────────────────────────────────────────
const keyframes = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Source+Sans+Pro:wght@400;600&display=swap');

  @keyframes float1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    25% { transform: translate(30px, -20px) scale(1.05); }
    50% { transform: translate(-10px, 20px) scale(0.95); }
    75% { transform: translate(20px, 10px) scale(1.02); }
  }

  @keyframes float2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(-25px, 15px) scale(1.03); }
    66% { transform: translate(15px, -25px) scale(0.97); }
  }

  @keyframes float3 {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(-20px, -15px); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
  }

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes scrollBounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(8px); }
  }

  @keyframes dashDraw {
    to { stroke-dashoffset: 0; }
  }

  .feature-card:hover {
    transform: translateY(-6px) scale(1.02) !important;
    box-shadow: 0 20px 40px rgba(74, 222, 128, 0.15) !important;
    border-color: #4ADE80 !important;
  }
`;

// ─── Styles ────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#1F2937',
    background: '#ffffff',
    overflowX: 'hidden',
  },

  // Nav
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  navInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
  },
  logoImg: {
    height: 32,
    objectFit: 'contain' as const,
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
  },
  navLink: {
    color: '#6B7280',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    transition: 'color 0.2s',
  },
  navCta: {
    background: '#4ADE80',
    color: '#fff',
    padding: '8px 20px',
    borderRadius: 8,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
    transition: 'all 0.2s',
  },

  // Hero
  hero: {
    position: 'relative' as const,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 40%, #f0fdfa 100%)',
  },
  heroBackground: {
    position: 'absolute' as const,
    inset: 0,
    overflow: 'hidden',
  },
  floatingNode: {
    position: 'absolute' as const,
    borderRadius: '50%',
    border: '3px solid rgba(74, 222, 128, 0.3)',
    transition: 'transform 0.3s ease-out',
  },
  node1: {
    width: 80,
    height: 80,
    top: '15%',
    left: '10%',
    animation: 'float1 8s ease-in-out infinite',
    background: 'radial-gradient(circle, transparent 50%, rgba(74, 222, 128, 0.08) 100%)',
  },
  node2: {
    width: 60,
    height: 60,
    top: '25%',
    right: '15%',
    animation: 'float2 10s ease-in-out infinite',
    background: 'radial-gradient(circle, transparent 50%, rgba(74, 222, 128, 0.06) 100%)',
  },
  node3: {
    width: 100,
    height: 100,
    bottom: '20%',
    left: '20%',
    animation: 'float3 12s ease-in-out infinite',
    background: 'radial-gradient(circle, transparent 50%, rgba(74, 222, 128, 0.05) 100%)',
  },
  node4: {
    width: 50,
    height: 50,
    top: '60%',
    right: '25%',
    animation: 'float1 9s ease-in-out infinite 1s',
    background: 'radial-gradient(circle, transparent 50%, rgba(45, 212, 191, 0.08) 100%)',
  },
  node5: {
    width: 70,
    height: 70,
    bottom: '30%',
    right: '10%',
    animation: 'float2 11s ease-in-out infinite 2s',
    background: 'radial-gradient(circle, transparent 50%, rgba(74, 222, 128, 0.06) 100%)',
  },
  heroSvg: {
    position: 'absolute' as const,
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: 0.15,
  },
  connectLine: {
    stroke: '#4ADE80',
    strokeWidth: 1.5,
    strokeDasharray: '8 4',
    animation: 'pulse 3s ease-in-out infinite',
  } as React.CSSProperties,
  heroContent: {
    position: 'relative' as const,
    zIndex: 10,
    textAlign: 'center' as const,
    padding: '0 24px',
    maxWidth: 800,
  },
  heroTitle: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    marginBottom: 24,
  },
  heroTitleLine1: {
    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    fontWeight: 300,
    color: '#374151',
    letterSpacing: '-0.02em',
  },
  heroTitleGradient: {
    fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #4ADE80 0%, #2dd4bf 50%, #4ADE80 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'gradientShift 4s ease infinite',
    letterSpacing: '-0.03em',
  },
  heroSubtitle: {
    fontSize: 'clamp(1rem, 2vw, 1.25rem)',
    color: '#6B7280',
    lineHeight: 1.7,
    maxWidth: 600,
    margin: '0 auto 40px',
  },
  heroCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    background: '#4ADE80',
    color: '#fff',
    padding: '16px 36px',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 30px rgba(74, 222, 128, 0.3)',
  },
  scrollIndicator: {
    position: 'absolute' as const,
    bottom: 40,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 24,
    height: 40,
    border: '2px solid rgba(74, 222, 128, 0.4)',
    borderRadius: 12,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 8,
  },
  scrollDot: {
    width: 4,
    height: 4,
    borderRadius: '50%',
    background: '#4ADE80',
    animation: 'scrollBounce 2s ease-in-out infinite',
  },

  // Metrics
  metricsSection: {
    padding: '80px 24px',
    background: '#f9fafb',
  },
  metricsGrid: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 32,
  },
  metricCard: {
    textAlign: 'center' as const,
    padding: 32,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    border: '1px solid rgba(0,0,0,0.06)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  metricValue: {
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 800,
    color: '#4ADE80',
    marginBottom: 8,
  },
  metricLabel: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: 500,
  },

  // Screenshots
  screenshotSection: {
    padding: '100px 24px',
  },
  sectionHeader: {
    textAlign: 'center' as const,
    marginBottom: 60,
  },
  sectionTitle: {
    fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: 12,
    letterSpacing: '-0.02em',
  },
  sectionSubtitle: {
    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
    color: '#6B7280',
    maxWidth: 500,
    margin: '0 auto',
  },
  screenshotContainer: {
    maxWidth: 1000,
    margin: '0 auto',
  },
  canvasMock: {
    background: '#1a1a2e',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  canvasToolbar: {
    padding: '12px 16px',
    background: '#16162a',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  toolbarDots: {
    display: 'flex',
    gap: 6,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
  },
  toolbarTitle: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: 500,
  },
  canvasArea: {
    position: 'relative' as const,
    height: 400,
    padding: 24,
  },
  mockNode: {
    position: 'absolute' as const,
    background: '#252540',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.1)',
    width: 180,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  mockNodeHeader: {
    padding: '6px 12px',
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    background: 'rgba(255,255,255,0.08)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  mockNodeBody: {
    padding: '10px 12px',
    fontSize: 11,
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 1.4,
  },
  mockNodeImage: {
    padding: 10,
  },
  mockImagePlaceholder: {
    width: '100%',
    height: 60,
    borderRadius: 6,
    background: 'rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
  },
  canvasSvg: {
    position: 'absolute' as const,
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none' as const,
  },
  mockEdge: {
    fill: 'none',
    stroke: 'rgba(74, 222, 128, 0.4)',
    strokeWidth: 2,
    strokeDasharray: '200',
    strokeDashoffset: '200',
    animation: 'dashDraw 2s ease forwards',
  } as React.CSSProperties,

  // Features
  featuresSection: {
    padding: '100px 24px',
    background: '#f9fafb',
  },
  featuresGrid: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 24,
  },
  featureCard: {
    background: '#fff',
    borderRadius: 16,
    padding: 32,
    border: '1px solid rgba(0,0,0,0.06)',
    transition: 'all 0.3s ease',
    cursor: 'default',
  },
  featureIcon: {
    fontSize: 32,
    marginBottom: 16,
  },
  featureTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
    color: '#1F2937',
  },
  featureDesc: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 1.6,
  },

  // Demo
  demoSection: {
    padding: '100px 24px',
  },
  videoContainer: {
    maxWidth: 900,
    margin: '0 auto',
  },
  videoPlaceholder: {
    position: 'relative' as const,
    paddingTop: '56.25%',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: 20,
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
  },
  playButton: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -60%)',
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: 'rgba(74, 222, 128, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 30px rgba(74, 222, 128, 0.4)',
    transition: 'transform 0.3s ease',
  },
  videoLabel: {
    position: 'absolute' as const,
    bottom: '25%',
    left: '50%',
    transform: 'translateX(-50%)',
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
  },

  // Testimonials
  testimonialsSection: {
    padding: '100px 24px',
    background: '#f9fafb',
  },
  testimonialsGrid: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 24,
  },
  testimonialCard: {
    background: '#fff',
    borderRadius: 16,
    padding: 32,
    border: '1px solid rgba(0,0,0,0.06)',
    position: 'relative' as const,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  quoteIcon: {
    fontSize: 48,
    color: '#4ADE80',
    opacity: 0.3,
    fontFamily: 'Georgia, serif',
    lineHeight: 1,
    marginBottom: 8,
  },
  testimonialQuote: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 1.7,
    marginBottom: 24,
    fontStyle: 'italic' as const,
  },
  testimonialAuthor: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  authorAvatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4ADE80, #2dd4bf)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 600,
    fontSize: 16,
  },
  authorName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1F2937',
  },
  authorRole: {
    fontSize: 13,
    color: '#6B7280',
  },

  // CTA
  ctaSection: {
    padding: '100px 24px',
    textAlign: 'center' as const,
    background: 'linear-gradient(135deg, #f0fdf4 0%, #f0fdfa 100%)',
  },
  ctaTitle: {
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: 16,
    letterSpacing: '-0.02em',
  },
  ctaSubtitle: {
    fontSize: 18,
    color: '#6B7280',
    marginBottom: 32,
  },
  ctaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    background: '#4ADE80',
    color: '#fff',
    padding: '16px 40px',
    borderRadius: 12,
    fontSize: 17,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 30px rgba(74, 222, 128, 0.3)',
  },

  // Footer
  footer: {
    padding: '48px 24px 24px',
    borderTop: '1px solid rgba(0,0,0,0.06)',
  },
  footerInner: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 24,
    marginBottom: 24,
  },
  footerBrand: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  footerText: {
    fontSize: 13,
    color: '#6B7280',
  },
  footerLinks: {
    display: 'flex',
    gap: 24,
  },
  footerLink: {
    color: '#6B7280',
    textDecoration: 'none',
    fontSize: 14,
    transition: 'color 0.2s',
  },
  footerCopyright: {
    textAlign: 'center' as const,
    fontSize: 12,
    color: '#9CA3AF',
    maxWidth: 1100,
    margin: '0 auto',
    paddingTop: 24,
    borderTop: '1px solid rgba(0,0,0,0.04)',
  },
};
